import { t } from "@travily/i18n";
import communityIllustration from "@ui/assets/illustrations/community-animated.webp";
import { Icon } from "@ui/components/Icon";
import { Wizard } from "@ui/components/Wizard";
import { WizardTextField } from "@ui/components/WizardTextField";
import type { ReactNode } from "react";

const COLOR = "yellow" as const;

export interface CommunityHandleStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Save and finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage. */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Current value of the handle field. */
  value: string;
  /** Field error for the handle input, or undefined when valid. */
  error?: string;
  /** Positive confirmation (e.g. "handle available"), shown once validated clean. */
  successMessage?: ReactNode;
  /** Called with the new value on every field change. */
  onChange: (value: string) => void;
  /** Called when the field loses focus (drives blur-time validation). */
  onBlur?: () => void;
}

export function CommunityHandleStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  value,
  error,
  successMessage,
  onChange,
  onBlur,
}: CommunityHandleStepViewProps) {
  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" className="h-32 w-auto" src={communityIllustration} />}
        heading={t("web.accounts.setup.steps.handle.title")}
        helper={t("web.accounts.setup.steps.handle.subtitle")}
      >
        <div className="w-full max-w-[600px]">
          <WizardTextField
            autoFocus
            color={COLOR}
            errorMessage={error}
            isInvalid={Boolean(error)}
            isLabelHidden
            label={t("web.accounts.setup.steps.handle.label")}
            leadIcon={<Icon name="username" size="20" type="sticker" />}
            onBlur={onBlur}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !primaryPending) onPrimary();
            }}
            placeholder={t("web.accounts.setup.steps.handle.placeholder")}
            successMessage={successMessage}
            value={value}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
